import { useEffect, useRef, useState } from "react";
import type { Track } from "../../shared/domain.ts";
import { decadeLabel } from "./labels.ts";

/**
 * The Reveal.
 *
 * The moment the answer lands is the only part of a Round with no interaction in
 * it, which makes it the one place the game can afford to be theatrical. It used
 * to be two lines of text. Now the artwork — which the Catalog has carried on
 * every Track since the first harvest and never once displayed — flips in, and
 * the card takes its accent colour from the sleeve so every song reveals in its
 * own palette.
 */

export interface SongCardProps {
  track: Track;
  /** "won" tints the card green and celebrates; "revealed" is the consolation. */
  outcome: "won" | "revealed";
  /** Steps the player spent. 1 means they got it from the opening Clip. */
  steps: number;
}

/**
 * Pulls a usable accent out of the sleeve.
 *
 * Deezer's artwork is same-origin-permissive, so the pixels can be read without
 * tainting the canvas. Sampling is coarse on purpose — one pass over a 12×12
 * downscale, skipping near-black and near-white so a dark sleeve does not yield
 * a black glow, and boosting saturation so muted covers still register.
 */
function useArtworkAccent(url: string | null): string | null {
  const [accent, setAccent] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setAccent(null);
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;

    img.onload = () => {
      if (cancelled) return;
      try {
        const size = 12;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, size, size);

        const { data } = ctx.getImageData(0, 0, size, size);
        let r = 0;
        let g = 0;
        let b = 0;
        let counted = 0;
        for (let i = 0; i < data.length; i += 4) {
          const [pr, pg, pb] = [data[i]!, data[i + 1]!, data[i + 2]!];
          const max = Math.max(pr, pg, pb);
          const min = Math.min(pr, pg, pb);
          // Skip the near-black and near-white that dominate most sleeves.
          if (max < 40 || min > 225) continue;
          r += pr;
          g += pg;
          b += pb;
          counted += 1;
        }
        if (counted === 0) return;

        // Push toward the colour's own hue so a washed-out average still reads.
        const avg = [r / counted, g / counted, b / counted];
        const mid = (Math.max(...avg) + Math.min(...avg)) / 2;
        const punchy = avg.map((c) => Math.round(Math.min(255, Math.max(0, mid + (c - mid) * 1.6))));
        setAccent(`rgb(${punchy[0]} ${punchy[1]} ${punchy[2]})`);
      } catch {
        // A tainted canvas or a blocked image just means no accent. The card
        // falls back to the app's own purple, which is never wrong.
      }
    };

    return () => {
      cancelled = true;
    };
  }, [url]);

  return accent;
}

function praise(outcome: "won" | "revealed", steps: number): string {
  if (outcome === "revealed") return "Out of guesses";
  if (steps === 1) return "First listen!";
  if (steps === 2) return "Two tries — sharp";
  if (steps <= 4) return `Got it in ${steps}`;
  return `Got there in ${steps}`;
}

export function SongCard({ track, outcome, steps }: SongCardProps) {
  const accent = useArtworkAccent(track.artworkUrl);
  // Keyed remount would restart the animation on every parent render, so the
  // flip is driven by a one-shot class instead.
  const cardRef = useRef<HTMLDivElement>(null);

  const flourish = outcome === "won" && steps === 1;

  return (
    <div
      ref={cardRef}
      className={`songcard songcard--${outcome}${flourish ? " songcard--flourish" : ""}`}
      style={accent ? ({ "--card-accent": accent } as React.CSSProperties) : undefined}
      role="status"
      aria-live="polite"
    >
      <div className="songcard__glow" aria-hidden="true" />

      <div className="songcard__inner">
        {track.artworkUrl ? (
          <img
            className="songcard__art"
            src={track.artworkUrl}
            alt=""
            width={96}
            height={96}
            loading="eager"
            decoding="async"
          />
        ) : (
          <div className="songcard__art songcard__art--empty" aria-hidden="true">
            ♪
          </div>
        )}

        <div className="songcard__text">
          <p className="songcard__verdict">{praise(outcome, steps)}</p>
          <p className="songcard__title">{track.title}</p>
          <p className="songcard__artist">{track.artist}</p>
          <p className="songcard__meta">
            <span className="songcard__chip">{decadeLabel(track.decade)}</span>
            {track.genres[0] && <span className="songcard__chip">{track.genres[0]}</span>}
          </p>
        </div>
      </div>

      {flourish && (
        <div className="songcard__confetti" aria-hidden="true">
          {Array.from({ length: 14 }, (_, i) => (
            <span key={i} style={{ "--i": i } as React.CSSProperties} />
          ))}
        </div>
      )}
    </div>
  );
}
